import { type ReactNode, useState, useMemo } from 'react';
import type { Table, ColumnDef } from '@tanstack/react-table';
import { X, Columns3, MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { DataTableFacetedFilter } from './DataTableFacetedFilter';
import { DataTableBooleanFilter } from './DataTableBooleanFilter';
import { DataTableTextFilter } from './DataTableTextFilter';
import { DataTableDateFilter } from './DataTableDateFilter';
import { DataTableDateRangeFilter } from './DataTableDateRangeFilter';
import { DataTableNumberRangeFilter } from './DataTableNumberRangeFilter';
import type { DataTableColumnMeta, ExtraFilter } from './types';

const MAX_VISIBLE_FILTERS = 10;

type FilterColumn<TFilter> = {
    columnId: string;
    filterKey: string;
    header: string;
    config: NonNullable<DataTableColumnMeta<TFilter>['filter']>;
};

type DataTableToolbarProps<TData, TFilter extends Record<string, unknown>> = {
    table: Table<TData>;
    columns: ColumnDef<TData, unknown>[];
    searchValue: string;
    onSearchChange: (value: string) => void;
    searchPlaceholder?: string;
    autoFocusSearch?: boolean;
    filterState: Partial<TFilter>;
    updateFilter: (key: string, value: unknown) => void;
    clearFilters: () => void;
    enableColumnVisibility?: boolean;
    toolbarActions?: ReactNode;
    extraFilters?: ExtraFilter[];
    onEnter?: () => void;
};

export function DataTableToolbar<TData, TFilter extends Record<string, unknown>>({
    table,
    columns,
    searchValue,
    onSearchChange,
    searchPlaceholder = 'Recherche...',
    autoFocusSearch = true,
    filterState,
    updateFilter,
    clearFilters,
    enableColumnVisibility = false,
    toolbarActions,
    extraFilters,
    onEnter,
}: DataTableToolbarProps<TData, TFilter>) {
    const [showAll, setShowAll] = useState(false);
    const hasActiveFilters = Object.keys(filterState).filter((k) => k !== 'searchValue').length > 0;

    // Collect filterable columns
    const filterableColumns = useMemo(
        () =>
            columns
                .map((col) => {
                    const meta = col.meta as DataTableColumnMeta<TFilter> | undefined;
                    if (!meta?.filter) return null;
                    const columnId = 'accessorKey' in col ? String(col.accessorKey) : 'id' in col ? String(col.id) : '';
                    const filterKey = meta.filter.filterKey ? String(meta.filter.filterKey) : columnId;
                    const header = typeof col.header === 'string' ? col.header : columnId;
                    return { columnId, filterKey, header, config: meta.filter };
                })
                .filter(Boolean) as FilterColumn<TFilter>[],
        [columns],
    );

    const visibleFilters = showAll ? filterableColumns : filterableColumns.slice(0, MAX_VISIBLE_FILTERS);
    const hasOverflow = filterableColumns.length > MAX_VISIBLE_FILTERS;
    const hasActiveHiddenFilter =
        hasOverflow &&
        filterableColumns
            .slice(MAX_VISIBLE_FILTERS)
            .some((f) => filterState[f.filterKey as keyof TFilter] !== undefined);

    const renderFilter = ({ filterKey, header, config }: FilterColumn<TFilter>) => {
        const value = filterState[filterKey as keyof TFilter];

        switch (config.type) {
            case 'select':
                return (
                    <DataTableFacetedFilter
                        key={filterKey}
                        title={header}
                        options={config.options || []}
                        selectedValues={Array.isArray(value) ? value : value ? [String(value)] : []}
                        onChange={(values) => updateFilter(filterKey, values.length > 0 ? values : undefined)}
                        renderOption={config.renderOption}
                    />
                );
            case 'multi-select':
                return (
                    <DataTableFacetedFilter
                        key={filterKey}
                        title={header}
                        options={config.options || []}
                        selectedValues={Array.isArray(value) ? value : []}
                        onChange={(values) => updateFilter(filterKey, values.length > 0 ? values : undefined)}
                        multi
                        renderOption={config.renderOption}
                    />
                );
            case 'boolean':
                return (
                    <DataTableBooleanFilter
                        key={filterKey}
                        title={header}
                        value={value as boolean | undefined}
                        onChange={(v) => updateFilter(filterKey, v)}
                    />
                );
            case 'date':
                return (
                    <DataTableDateFilter
                        key={filterKey}
                        title={header}
                        value={(value as string) || ''}
                        onChange={(v) => updateFilter(filterKey, v || undefined)}
                    />
                );
            case 'date-range':
                return (
                    <DataTableDateRangeFilter
                        key={filterKey}
                        title={header}
                        value={value as { gte?: number; lte?: number } | undefined}
                        onChange={(v) => updateFilter(filterKey, v)}
                    />
                );
            case 'number-range':
                return (
                    <DataTableNumberRangeFilter
                        key={filterKey}
                        title={header}
                        value={value as { gte?: number; lte?: number } | undefined}
                        onChange={(v) => updateFilter(filterKey, v)}
                    />
                );
            case 'text':
            case 'number':
                return (
                    <DataTableTextFilter
                        key={filterKey}
                        title={header}
                        value={(value as string) || ''}
                        onChange={(v) => updateFilter(filterKey, v || undefined)}
                        placeholder={config.placeholder}
                        type={config.type === 'number' ? 'number' : 'text'}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="flex flex-col gap-2 py-2">
            {/* Search — full width */}
            <Input
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') onEnter?.();
                }}
                className="h-8 w-full"
                autoFocus={autoFocusSearch}
            />

            {/* Filters + column visibility */}
            <div className="flex flex-wrap items-center gap-2">
                {(extraFilters ?? []).map((extra) =>
                    renderFilter({
                        columnId: extra.key,
                        filterKey: extra.key,
                        header: extra.title,
                        config:
                            extra.type === 'text'
                                ? { type: 'text', filterKey: extra.key, placeholder: extra.placeholder }
                                : { type: extra.type, filterKey: extra.key, options: extra.options },
                    }),
                )}

                {visibleFilters.map(renderFilter)}

                {hasOverflow && !showAll && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className={cn('h-8 border-dashed bg-filter', hasActiveHiddenFilter && 'bg-filter-active')}
                        onClick={() => setShowAll(true)}
                    >
                        <MoreHorizontal className="size-3.5" />+{filterableColumns.length - MAX_VISIBLE_FILTERS}
                    </Button>
                )}

                {hasOverflow && showAll && (
                    <Button type="button" variant="ghost" size="sm" className="h-8" onClick={() => setShowAll(false)}>
                        Moins
                    </Button>
                )}

                {hasActiveFilters && (
                    <Button type="button" variant="ghost" size="sm" className="h-8" onClick={clearFilters}>
                        Effacer
                        <X className="ml-1 size-3.5" />
                    </Button>
                )}

                <div className="ml-auto flex items-center gap-2">
                    {toolbarActions}

                    {enableColumnVisibility && (
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button type="button" variant="outline" size="sm" className="h-8">
                                    <Columns3 className="size-3.5" />
                                    Colonnes
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[220px] p-0" align="end">
                                <Command>
                                    <CommandInput placeholder="Rechercher..." />
                                    <CommandList>
                                        <CommandGroup>
                                            {table
                                                .getAllColumns()
                                                .filter((col) => col.getCanHide())
                                                .map((col) => (
                                                    <CommandItem
                                                        key={col.id}
                                                        onSelect={() => col.toggleVisibility(!col.getIsVisible())}
                                                        data-checked={col.getIsVisible()}
                                                    >
                                                        <span>
                                                            {typeof col.columnDef.header === 'string'
                                                                ? col.columnDef.header
                                                                : col.id}
                                                        </span>
                                                    </CommandItem>
                                                ))}
                                        </CommandGroup>
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    )}
                </div>
            </div>
        </div>
    );
}
